<!--
  SaveAsSetButton.vue -- Phase 4.9 product-editor "Save as decoration
  set" affordance (Phase 1 coordination hook).

  Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
  Pebble: Phase 4.9 -- SaveAsSetButton on product editor.
  Plan ref: planning/design-handoffs/handoff_2026-06-13/
            implementation-plan/05-reuse-scale.md (build order step 18 +
            Surface inventory "SaveAsSetButton.vue").

  What it does
  ------------
  Renders a button at the bottom of the product-editor Placements section
  (was the Decorations section header until the 2026-07-15 left-rail IA
  restructure). Clicking opens a modal that captures the configured product's
  decorations as a new DecorationSet template via the snapshot-from-
  product path on POST /api/decoration_sets/ (origin='configured_product'
  + source_campaign_product_id). The placements field on the body is
  intentionally NOT sent -- the backend ignores it when the snapshot
  path is taken (per ADR 0044 + Phase 4.1 service contract).

  P5 PR-G -- zone-class capture
  -----------------------------
  The client payload is unchanged: this surface still sends only
  origin + source_campaign_product_id + name/description/client_tag.
  The behavioural change lives on the BACKEND: the snapshot service
  (decoration_set_service._snapshot_placements_from_product) now
  resolves each source decoration's placement_zone.class_id and stamps
  placement_zone_class_id on the captured DecorationSetPlacement rows.
  Recipes saved via this button now apply correctly to other products
  in the same zone-class even when the specific zones differ
  (e.g. recipe captured against left_chest applies to a product whose
  only chest-class zone is right_chest -- both share chest-class).
  The user-visible UX is unchanged.

  Per P4-Q10
  ----------
    - Name pre-fills "{product_name} - decorations".
      * Project ASCII-only rule -> regular hyphen-space-hyphen-space,
        NOT an em-dash.
    - Auto-tag client: if the product's store has a single client, the
      new set's client_tag pre-fills to that client's display name.
      Multi-client stores leave client_tag empty with a "no tag" note.
    - Disabled when the product has zero decorations -- the snapshot
      would be empty and the set would be useless. A title tooltip
      explains.

  Behavioural surface
  -------------------
  Tests pin to the data-testid attributes below. Selectors are stable
  across css refactors (project convention: no nth-child / text-based
  selectors).

    save-as-set-trigger          -- the always-rendered button.
    save-as-set-modal            -- the modal root (v-if open).
    save-as-set-name             -- name input.
    save-as-set-description      -- description input.
    save-as-set-client-tag       -- client tag input.
    save-as-set-multi-client-note -- note rendered when the store has no client.
    save-as-set-submit           -- submit button inside the modal.
    save-as-set-cancel           -- cancel button inside the modal.
    save-as-set-error            -- error banner inside the modal on failure.
    save-as-set-view-link        -- "View set" affordance shown after success.

  Why a thin modal vs. v-dialog/v-snackbar
  ----------------------------------------
  The sister DecorationSetEditModal under
  pages/distributor/decoration-sets/ uses the same hand-rolled
  .modal-backdrop pattern (no v-dialog). We match it here so the style
  stays consistent and the tests don't have to wrestle with the
  Vuetify portal mount semantics in jsdom.
-->
<template>
    <span class="save-as-set-root">
        <button
            type="button"
            class="save-as-set-trigger"
            data-testid="save-as-set-trigger"
            :disabled="!hasDecorations"
            :title="triggerTitle"
            @click="onOpen"
        >
            Save decoration set
        </button>

        <div
            v-if="open"
            class="save-as-set-backdrop"
            data-testid="save-as-set-modal"
            @click.self="onCancel"
        >
            <div
                class="save-as-set-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="save-as-set-title"
            >
                <header class="save-as-set-header">
                    <h3 id="save-as-set-title" class="save-as-set-title">
                        Save as decoration set
                    </h3>
                    <button
                        type="button"
                        class="save-as-set-close"
                        data-testid="save-as-set-cancel"
                        aria-label="Close"
                        @click="onCancel"
                    >
                        &times;
                    </button>
                </header>

                <form class="save-as-set-body" @submit.prevent="onSubmit">
                    <div class="save-as-set-field">
                        <label class="save-as-set-label" for="save-as-set-name">
                            Name
                        </label>
                        <input
                            id="save-as-set-name"
                            v-model="form.name"
                            type="text"
                            class="save-as-set-input"
                            data-testid="save-as-set-name"
                            required
                        />
                    </div>

                    <div class="save-as-set-field">
                        <label
                            class="save-as-set-label"
                            for="save-as-set-description"
                        >
                            Description (optional)
                        </label>
                        <input
                            id="save-as-set-description"
                            v-model="form.description"
                            type="text"
                            class="save-as-set-input"
                            data-testid="save-as-set-description"
                        />
                    </div>

                    <div class="save-as-set-field">
                        <label
                            class="save-as-set-label"
                            for="save-as-set-client-tag"
                        >
                            Client tag (optional)
                        </label>
                        <input
                            id="save-as-set-client-tag"
                            v-model="form.clientTag"
                            type="text"
                            class="save-as-set-input"
                            placeholder="e.g. Acme Corp"
                            data-testid="save-as-set-client-tag"
                        />
                        <div
                            v-if="multiClientStore"
                            class="save-as-set-hint"
                            data-testid="save-as-set-multi-client-note"
                        >
                            (no client tag - store is multi-client)
                        </div>
                    </div>

                    <div
                        v-if="errorMessage"
                        class="save-as-set-error"
                        data-testid="save-as-set-error"
                        role="alert"
                    >
                        {{ errorMessage }}
                    </div>

                    <div
                        v-if="createdSet"
                        class="save-as-set-success"
                        data-testid="save-as-set-success"
                    >
                        Decoration set "{{ createdSet.name }}" created.
                        <a
                            class="save-as-set-view-link"
                            data-testid="save-as-set-view-link"
                            :href="viewLink"
                        >View set</a>
                    </div>

                    <footer class="save-as-set-footer">
                        <button
                            type="button"
                            class="save-as-set-btn save-as-set-btn--ghost"
                            @click="onCancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            class="save-as-set-btn save-as-set-btn--primary"
                            data-testid="save-as-set-submit"
                            :disabled="submitting || !form.name.trim()"
                            @click="onSubmit"
                        >
                            <span v-if="!submitting">Save as set</span>
                            <span v-else>Saving...</span>
                        </button>
                    </footer>
                </form>
            </div>
        </div>
    </span>
</template>

<script>
/**
 * Component module export. Defined via the Options API for parity with
 * the sister DecorationSetEditModal -- the data-testid-driven tests
 * don't care which API style we use, but matching the rest of the
 * decoration-sets surface keeps the codebase consistent.
 */
import { useProductEditorStore } from "@/stores/productEditor";
import api from "@/services/api";

export default {
    name: "SaveAsSetButton",
    emits: ["created"],
    data() {
        return {
            store: useProductEditorStore(),
            open: false,
            submitting: false,
            errorMessage: "",
            createdSet: null,
            // The store's client name resolved on modal open (auto-tag
            // pre-fill source). ``null`` means "not resolved yet";
            // empty string means "store has no client" (multi-client).
            resolvedClientName: null,
            form: {
                name: "",
                description: "",
                clientTag: "",
            },
        };
    },
    computed: {
        /**
         * Read decoration count off the productEditor store. The store
         * exposes ``decorations`` as a computed ref of merged
         * server+pending rows. Tests pin a plain Array via
         * Object.defineProperty so we don't care which kind of ref
         * backs it -- just use ``.length``.
         */
        hasDecorations() {
            const decos = this.store?.decorations;
            return Array.isArray(decos) && decos.length > 0;
        },
        triggerTitle() {
            if (this.hasDecorations) {
                return "Save these decorations as a reusable set so you can easily apply them together to other products in your store";
            }
            return "Add decorations before saving as a set";
        },
        productName() {
            const name = this.store?.getEffectiveAttribute?.("product_name");
            return typeof name === "string" && name.trim()
                ? name.trim()
                : "Untitled product";
        },
        campaignProductId() {
            return this.store?.campaignProductId ?? null;
        },
        storeId() {
            const attrs = this.store?.serverState?.data?.attributes;
            if (!attrs) return null;
            return attrs.store_id ?? attrs.campaign_id ?? null;
        },
        multiClientStore() {
            // The modal renders the "multi-client" note ONLY after the
            // resolution finished and surfaced a null/empty client name.
            // While the resolution is in flight (resolvedClientName ===
            // null) we render neither the tag nor the note -- the user
            // sees the field empty + no hint until the lookup lands.
            return this.resolvedClientName === "";
        },
        viewLink() {
            if (!this.createdSet?.id) return "";
            return `/distributor/decorations/sets/${this.createdSet.id}`;
        },
    },
    methods: {
        async onOpen() {
            if (!this.hasDecorations) return;
            this.errorMessage = "";
            this.createdSet = null;
            this.open = true;
            this.form = {
                name: `${this.productName} - decorations`,
                description: "",
                clientTag: "",
            };
            // Kick off the client lookup. The modal renders immediately
            // with the name + description pre-filled; the client_tag
            // pre-fill lands when the GET completes.
            await this.resolveClientTag();
        },
        async resolveClientTag() {
            const sid = this.storeId;
            if (sid == null) {
                this.resolvedClientName = "";
                return;
            }
            try {
                // SAFRS-style GET /stores/<id>?include=client. The
                // backend returns the resource + an `included` array
                // containing the related client (when present). A
                // multi-client store has client_id=null and the
                // included array stays empty.
                const res = await api.get(`/stores/${sid}`, {
                    params: { include: "client" },
                });
                const body = res?.data || {};
                const included = Array.isArray(body.included)
                    ? body.included
                    : [];
                const clientEntry = included.find(
                    (e) => e?.type === "clients" || e?.type === "client",
                );
                const clientName = clientEntry?.attributes?.name;
                if (typeof clientName === "string" && clientName.trim()) {
                    this.resolvedClientName = clientName.trim();
                    this.form.clientTag = this.resolvedClientName;
                } else {
                    this.resolvedClientName = "";
                }
            } catch (_e) {
                // A failed lookup falls back to the multi-client UX --
                // we can't prove there's a single client, so we don't
                // pre-fill one. The user can still type a tag manually.
                this.resolvedClientName = "";
            }
        },
        async onSubmit() {
            if (this.submitting) return;
            if (!this.form.name.trim()) return;
            this.errorMessage = "";
            this.submitting = true;
            try {
                // Coerce to integer: route params arrive as strings even
                // when the underlying id is numeric. The backend rejects
                // strings (isinstance(..., int) gate). Coerce client-side
                // so the API surface stays strict.
                const cpIdRaw = this.campaignProductId;
                const cpId =
                    cpIdRaw == null || cpIdRaw === ""
                        ? null
                        : typeof cpIdRaw === "number"
                            ? cpIdRaw
                            : Number(cpIdRaw);
                const body = {
                    name: this.form.name.trim(),
                    description: this.form.description.trim() || null,
                    origin: "configured_product",
                    source_campaign_product_id: cpId,
                };
                const tag = this.form.clientTag.trim();
                if (tag) {
                    body.client_tag = tag;
                }
                const res = await api.post("/decoration_sets/", body);
                const created = res?.data || null;
                this.createdSet = created;
                this.$emit("created", created);
            } catch (e) {
                // Surface a readable message; keep the modal open so
                // the user can retry without losing their inputs.
                const serverMsg = e?.response?.data?.error;
                this.errorMessage =
                    typeof serverMsg === "string" && serverMsg
                        ? serverMsg
                        : e?.message || "Failed to save decoration set.";
            } finally {
                this.submitting = false;
            }
        },
        onCancel() {
            this.open = false;
            this.errorMessage = "";
            this.createdSet = null;
            this.resolvedClientName = null;
            this.form = { name: "", description: "", clientTag: "" };
        },
    },
};
</script>

<style scoped>
.save-as-set-root {
    display: inline-flex;
}

.save-as-set-trigger {
    appearance: none;
    background: transparent;
    border: 1px solid var(--tf-border, #d0d0d0);
    border-radius: 4px;
    /* 10px L/R: the button now sits at the bottom of the Placements section
     * (2026-07-15) rather than the tight header row, so it has room for the
     * roomier padding again (was briefly 5px in #2072 to avoid wrap). */
    padding: 4px 10px;
    font-size: 12px;
    color: var(--tf-text-default, #1f1f1f);
    cursor: pointer;
}

.save-as-set-trigger:not(:disabled):hover {
    background: var(--tf-surface-hover, rgba(0, 0, 0, 0.04));
}

.save-as-set-trigger:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.save-as-set-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.save-as-set-modal {
    background: #ffffff;
    border-radius: 6px;
    width: min(480px, 92vw);
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
}

.save-as-set-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid var(--tf-border, #e0e0e0);
}

.save-as-set-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
}

.save-as-set-close {
    appearance: none;
    background: transparent;
    border: none;
    font-size: 22px;
    cursor: pointer;
    color: var(--tf-text-muted, #6b6b6b);
}

.save-as-set-body {
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.save-as-set-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.save-as-set-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--tf-text-muted, #5b5b5b);
}

.save-as-set-input {
    width: 100%;
    padding: 8px 10px;
    font-size: 14px;
    border: 1px solid var(--tf-border, #d0d0d0);
    border-radius: 4px;
    box-sizing: border-box;
}

.save-as-set-hint {
    font-size: 11px;
    color: var(--tf-text-muted, #6b6b6b);
}

.save-as-set-error {
    background: rgba(220, 53, 69, 0.08);
    border: 1px solid rgba(220, 53, 69, 0.4);
    color: rgb(140, 28, 38);
    padding: 8px 10px;
    border-radius: 4px;
    font-size: 13px;
}

.save-as-set-success {
    background: rgba(40, 167, 69, 0.08);
    border: 1px solid rgba(40, 167, 69, 0.4);
    color: rgb(20, 90, 40);
    padding: 8px 10px;
    border-radius: 4px;
    font-size: 13px;
}

.save-as-set-view-link {
    margin-left: 8px;
    color: var(--tf-brand-primary, #1f6feb);
    text-decoration: underline;
}

.save-as-set-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--tf-border, #f0f0f0);
    margin-top: 4px;
}

.save-as-set-btn {
    appearance: none;
    border-radius: 4px;
    padding: 6px 14px;
    font-size: 13px;
    cursor: pointer;
    border: 1px solid transparent;
}

.save-as-set-btn--ghost {
    background: transparent;
    color: var(--tf-text-muted, #6b6b6b);
    border-color: var(--tf-border, #e0e0e0);
}

.save-as-set-btn--primary {
    background: var(--tf-brand-primary, #1f6feb);
    color: #ffffff;
    border-color: transparent;
}

.save-as-set-btn--primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
